import React, { useState, useEffect } from "react";
import { trackPurchase } from "../redesign/store/analytics";
import { useSearchParams, Link } from "react-router-dom";
import { useOpeningHours } from "../redesign/useBusinessInfo";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { 
  CheckCircle, 
  Package, 
  Mail, 
  CreditCard,
  ArrowRight,
  Loader2,
  AlertCircle
} from "lucide-react";
import { useCart } from "../contexts/CartContext";
import SEOHead from "../components/SEOHead";

const CheckoutSuccessPage = () => {
  const openingHours = useOpeningHours();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { clearCart } = useCart();

  useEffect(() => {
    if (sessionId) {
      fetchPaymentStatus();
    } else {
      setError("No session ID found");
      setLoading(false);
    }
  }, [sessionId]);

  const fetchPaymentStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || `Server error: ${response.status}`;
        console.error('Payment status error:', errorMessage);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Payment status received:', data);
      setPaymentStatus(data);
      
      // Clear cart if payment was successful
      if (data.payment_status === "paid") {
        trackPurchase(data);
        try {
          clearCart();
        } catch (error) {
          console.warn('Could not clear cart:', error);
          // Don't fail the whole component if cart clearing fails
        }
      }

    } catch (err) {
      console.error('Error fetching payment status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEOHead
          title="Order Confirmation - Umoona Opal Mine"
          description="Thank you for your purchase from Umoona Opal Mine. Your order confirmation details."
          canonical="/checkout/success"
        />
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (error || !paymentStatus) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <SEOHead
          title="Order Status - Umoona Opal Mine"
          description="Check your order status from Umoona Opal Mine."
          canonical="/checkout/success"
        />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-red-200">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Status Unknown</h1>
              <p className="text-gray-600 mb-6">
                {error || "We couldn't verify your payment status. Please contact us for assistance."}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild variant="outline">
                  <Link to="/contact">Contact Support</Link>
                </Button>
                <Button asChild className="mining-cta">
                  <Link to="/opal-store">Return to Store</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isSuccess = paymentStatus.payment_status === "paid";

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEOHead
        title={isSuccess ? "Order Successful - Umoona Opal Mine" : "Payment Processing - Umoona Opal Mine"}
        description="Thank you for your purchase from Umoona Opal Mine. Your order has been received."
        canonical="/checkout/success"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <Card className={`${isSuccess ? 'border-green-200' : 'border-yellow-200'} shadow-lg`}>
          <CardHeader className="text-center pb-6">
            <div className={`w-20 h-20 ${isSuccess ? 'bg-green-100' : 'bg-yellow-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              {isSuccess ? (
                <CheckCircle className="w-10 h-10 text-green-600" />
              ) : (
                <AlertCircle className="w-10 h-10 text-yellow-600" />
              )}
            </div>
            
            <CardTitle className="text-3xl mb-2">
              {isSuccess ? "Payment Successful!" : "Payment Processing"}
            </CardTitle>
            
            <p className="text-gray-600 text-lg">
              {isSuccess 
                ? "Thank you for your purchase from Umoona Opal Mine & Museum" 
                : "Your payment is being processed"
              }
            </p>
            
            {isSuccess && (
              <Badge className="bg-green-100 text-green-800 border-green-200 mt-4">
                Order Confirmed
              </Badge>
            )}
          </CardHeader>

          <CardContent className="space-y-6">
            
            {/* Payment Details */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment Details
              </h3>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Amount Paid:</span>
                  <p className="font-semibold text-lg text-purple-600">
                    {formatCurrency(paymentStatus.amount)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Currency:</span>
                  <p className="font-semibold">{paymentStatus.currency.toUpperCase()}</p>
                </div>
                <div>
                  <span className="text-gray-600">Session ID:</span>
                  <p className="font-mono text-xs break-all">{paymentStatus.session_id}</p>
                </div>
                <div>
                  <span className="text-gray-600">Date:</span>
                  <p className="font-semibold">{formatDate(paymentStatus.created_at)}</p>
                </div>
              </div>

              {paymentStatus.customer_email && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-gray-600">Confirmation Email:</span>
                  <p className="font-semibold">{paymentStatus.customer_email}</p>
                </div>
              )}
            </div>

            {isSuccess && (
              <>
                {/* What Happens Next */}
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    What Happens Next
                  </h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                      <div>
                        <p className="font-medium">Order Processing</p>
                        <p className="text-gray-600">We'll prepare your authentic Coober Pedy opals for shipping</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                      <div>
                        <p className="font-medium">Shipping Notification</p>
                        <p className="text-gray-600">You'll receive tracking information via email</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                      <div>
                        <p className="font-medium">Certificate of Guarantee</p>
                        <p className="text-gray-600">Each opal includes our certificate of guarantee</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-purple-600" />
                    Need Help?
                  </h3>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    If you have any questions about your order, please don't hesitate to contact us:
                  </p>
                  
                  <div className="text-sm space-y-2">
                    <p><strong>Phone:</strong> (08) 8672 5288</p>
                    <p><strong>Email:</strong> admin@umoonaopalmine.com.au</p>
                    <p><strong>Hours:</strong> {openingHours.summary.join(" · ")}</p>
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isSuccess ? (
                <>
                  <Button asChild variant="outline" size="lg">
                    <Link to="/opal-store">
                      Continue Shopping
                    </Link>
                  </Button>
                  <Button asChild className="mining-cta" size="lg">
                    <Link to="/contact">
                      Visit Our Museum
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </>
              ) : (
                <Button onClick={fetchPaymentStatus} size="lg">
                  <Loader2 className="w-4 h-4 mr-2" />
                  Check Status Again
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;